/**
 * NORMALIZAREA FRAZEI - determinista, fara dictionare din afara.
 *
 *  - fara diacritice, litere mici (ș/ş, ț/ţ, ă, â, î);
 *  - cuvinte: litere si cifre, cu `-` si `.` lipite intre ele (`7-zip`, `21h1`,
 *    `2026-09-01`); terminatiile cu cratima ale romanei (`patch-uri`, `PC-ul`) cad;
 *  - radacina: articolul si pluralul se taie dupa o tabela de terminatii, apoi
 *    vocalele de la coada. Aceeasi functie se aplica si frazei, si
 *    vocabularului, deci "calculatoarele", "calculatorului" si "calculator"
 *    dau toate `calculator`, iar "sediului", "sediile" si "sediu" dau `sed`.
 *
 * Radacina NU e un cuvant corect - e doar o cheie de potrivire. Se compara
 * numai radacini intre ele.
 */

export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[șş]/g, 's')
    .replace(/[țţ]/g, 't')
    .toLowerCase()
}

export interface Token {
  /** Cuvantul, fara diacritice, litere mici. */
  word: string
  /** Radacina, pentru potrivire. */
  stem: string
  /** Locul in fraza originala, pentru subliniere. */
  start: number
  end: number
}

const HYPHEN_TAIL = /-(urilor|urile|uri|ului|ul|ilor|iile|ii|le|lor)\b/g

export function tokenize(text: string): Token[] {
  const folded = fold(text)
  const out: Token[] = []
  /* `%` e un cuvant: "% SLA". */
  const re = /[a-z0-9]+(?:[-.][a-z0-9]+)*|%/g
  /* Terminatia cu cratima se taie, dar locul cuvantului ramane cel din fraza. */
  const cleaned = folded.replace(HYPHEN_TAIL, (m) => ' '.repeat(m.length))
  for (let m = re.exec(cleaned); m !== null; m = re.exec(cleaned)) {
    const word = m[0]
    out.push({ word, stem: stem(word), start: m.index, end: m.index + word.length })
  }
  return out
}

const SUFFIXES = [
  'urilor',
  'urile',
  'ilor',
  'elor',
  'ului',
  'iile',
  'iei',
  'uri',
  'ele',
  'ile',
  'lor',
  'lui',
  'ies',
  'ul',
  'ua',
  'ea',
  'ii',
  'ei',
  'es',
]

export function stem(word: string): string {
  let w = fold(word)
  if (/^[0-9]/.test(w) || w.length <= 2) return w
  for (const suffix of SUFFIXES) {
    if (w.endsWith(suffix) && w.length - suffix.length >= 3) {
      w = w.slice(0, -suffix.length)
      break
    }
  }
  if (w.length > 3 && w.endsWith('s') && !/(ss|us|is|os)$/.test(w)) w = w.slice(0, -1)
  while (w.length > 3 && /[aeiuy]$/.test(w)) w = w.slice(0, -1)
  /* "resurse" -> "resurs" -> "resur", ca "resurselor" (pluralul dupa ce a cazut vocala). */
  if (w.length > 4 && w.endsWith('s') && !/(ss|us|is|os)$/.test(w)) w = w.slice(0, -1)
  if (w.endsWith('oar')) w = w.slice(0, -3) + 'or'
  if (w.endsWith('ien')) w = w.slice(0, -3) + 'ian'
  return w
}

/** Radacinile unei expresii, cu spatiu intre ele. */
export function stemPhrase(text: string): string {
  return tokenize(text)
    .map((t) => t.stem)
    .join(' ')
}

/** Distanta de editare (Levenshtein), oprita devreme peste `max`. */
export function distance(a: string, b: string, max = 3): number {
  if (Math.abs(a.length - b.length) > max) return max + 1
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    const cur = [i]
    let best = i
    for (let j = 1; j <= b.length; j++) {
      const v = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1))
      cur.push(v)
      if (v < best) best = v
    }
    if (best > max) return max + 1
    prev = cur
  }
  return prev[b.length]!
}

/** Cat de departe poate fi o greseala de tastare, dupa lungimea cuvantului. */
export function typoBudget(word: string): number {
  if (/[0-9]/.test(word) || word.length < 5) return 0
  return word.length >= 8 ? 2 : 1
}
